import React, { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Badge, Input, useToast } from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { UserItem, UserCreatePayload } from '../api/usersApi'
import { usersApi } from '../api/usersApi'

const FILTERS = [
  {
    id: 'isActive',
    label: 'Durum',
    value: 'all',
    options: [
      { label: 'Tümü', value: 'all' },
      { label: 'Aktif', value: 'true' },
      { label: 'Pasif', value: 'false' },
    ],
    widthClassName: 'min-w-[120px]' as const,
  },
]

type Form = {
  userName: string
  email: string
  phone: string
  password: string
  tenantId: string
}

export default function UsersPage() {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const tenantIdFromUrl = searchParams.get('tenantId')

  const listFn = useCallback(
    async (p: Parameters<Parameters<typeof CrudPage<UserItem, Form, UserCreatePayload, UserCreatePayload>>[0]['list']>[0]) => {
      const tenantId = tenantIdFromUrl
        ? Number(tenantIdFromUrl)
        : (p.filters.tenantId ? Number(p.filters.tenantId) : undefined)
      const res = await usersApi.list({
        search: p.search || undefined,
        tenantId,
        isActive: p.filters.isActive === 'all' ? undefined : p.filters.isActive === 'true',
        page: p.page,
        pageSize: p.pageSize,
        sortBy: p.sortBy,
        sortDir: p.sortDir,
      })
      return {
        items: res.items,
        totalCount: res.totalCount,
        page: res.page,
        pageSize: res.pageSize,
      }
    },
    [tenantIdFromUrl],
  )

  const columns = useMemo(
    () => [
        {
          key: 'userName',
          header: 'Kullanıcı adı',
          sortable: true,
          widthClassName: 'w-[160px]',
          render: (u: UserItem) => <span className="font-medium">{u.userName}</span>,
        },
        {
          key: 'email',
          header: 'E-posta',
          sortable: true,
          widthClassName: 'w-[220px]',
          render: (u: UserItem) => u.email ?? '—',
        },
        {
          key: 'phone',
          header: 'Telefon',
          sortable: false,
          widthClassName: 'w-[130px]',
          render: (u: UserItem) => u.phone ?? '—',
        },
        {
          key: 'tenantId',
          header: 'Tenant',
          sortable: true,
          widthClassName: 'w-[100px]',
          render: (u: UserItem) => (u.tenantId != null ? String(u.tenantId) : '—'),
        },
        {
          key: 'isActive',
          header: 'Durum',
          sortable: true,
          widthClassName: 'w-[100px]',
          render: (u: UserItem) =>
            u.isActive ? (
              <Badge variant="success">Aktif</Badge>
            ) : (
              <Badge variant="warning">Pasif</Badge>
            ),
        },
        {
          key: 'lastUpdated',
          header: 'Güncellenme',
          sortable: true,
          widthClassName: 'w-[180px]',
          render: (u: UserItem) =>
            u.lastUpdated
              ? new Date(u.lastUpdated).toLocaleString('tr-TR')
              : u.createdAt
                ? new Date(u.createdAt).toLocaleString('tr-TR')
                : '—',
        },
      ],
    [],
  )

  return (
    <CrudPage<UserItem, Form, UserCreatePayload, UserCreatePayload>
      title="Kullanıcılar"
      subtitle="Sistem kullanıcılarını görüntüleyin ve yeni kullanıcı oluşturun"
      primaryActionLabel="Yeni Kullanıcı"
      list={listFn}
      getRowId={(u) => u.userId}
      columns={columns}
      searchPlaceholder="Ara (kullanıcı adı, e-posta, telefon)…"
      filters={FILTERS}
      initialPageSize={20}
      modalTitles={{ create: 'Yeni Kullanıcı', edit: 'Kullanıcıyı Düzenle' }}
      getCreateDefaultForm={() => ({
        userName: '',
        email: '',
        phone: '',
        password: '',
        tenantId: '',
      })}
      getEditForm={(u) => ({
        userName: u.userName,
        email: u.email ?? '',
        phone: u.phone ?? '',
        password: '',
        tenantId: u.tenantId != null ? String(u.tenantId) : '',
      })}
      validate={(f) => {
        const err: Record<string, string | undefined> = {}
        if (!f.userName.trim()) err.userName = 'Kullanıcı adı zorunludur'
        if (f.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
          err.email = 'Geçerli bir e-posta girin'
        return err
      }}
      renderForm={({ mode, form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Kullanıcı adı"
            value={form.userName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, userName: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.userName}
            helperText={touched ? errors.userName : undefined}
            required
            disabled={isSubmitting || mode === 'edit'}
            placeholder="örn: ahmet.yilmaz"
            leftIcon={<i className="material-symbols-outlined text-lg">person</i>}
          />
          <Input
            label="E-posta"
            type="email"
            value={form.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, email: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.email}
            helperText={touched ? errors.email : undefined}
            disabled={isSubmitting}
            placeholder="kullanici@ornek.com"
            leftIcon={<i className="material-symbols-outlined text-lg">mail</i>}
          />
          <Input
            label="Telefon"
            value={form.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, phone: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            disabled={isSubmitting}
            placeholder="+90 5XX XXX XX XX"
            leftIcon={<i className="material-symbols-outlined text-lg">phone</i>}
          />
          {mode === 'create' && (
            <Input
              label="Şifre"
              type="password"
              value={form.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((s) => ({ ...s, password: e.target.value }))
              }
              onBlur={() => setTouched(true)}
              disabled={isSubmitting}
              placeholder="••••••••"
              helperText="Boş bırakılırsa kullanıcı şifresiz oluşturulur (önerilmez)"
              leftIcon={<i className="material-symbols-outlined text-lg">lock</i>}
            />
          )}
        </div>
      )}
      buildCreatePayload={(f) => ({
        userName: f.userName.trim(),
        email: f.email?.trim() || null,
        phone: f.phone?.trim() || null,
        password: f.password?.trim() || null,
        tenantId: f.tenantId ? Number(f.tenantId) : null,
      })}
      buildUpdatePayload={(f) => ({
        userName: f.userName.trim(),
        email: f.email?.trim() || null,
        phone: f.phone?.trim() || null,
        password: f.password?.trim() || null,
        tenantId: f.tenantId ? Number(f.tenantId) : null,
      })}
      create={(payload) => usersApi.create(payload)}
      update={async () => {
        toast({
          title: 'Bilgi',
          description: 'Kullanıcı düzenleme özelliği yakında eklenecektir.',
          variant: 'default',
        })
        throw new Error('Kullanıcı düzenleme henüz kullanılamıyor.')
      }}
      remove={async () => {
        toast({
          title: 'Bilgi',
          description: 'Kullanıcı silme özelliği yakında eklenecektir.',
          variant: 'default',
        })
        throw new Error('Kullanıcı silme henüz kullanılamıyor.')
      }}
      getCreatedId={(res) => res?.id}
    />
  )
}
